import { useState } from "react";
import type { LessonRetentionDetail } from "../../../generated/graphql";

interface LessonRetentionTableProps {
  lessons: LessonRetentionDetail[];
  onSort?: (field: string, order: string) => void;
}

export function LessonRetentionTable({ lessons, onSort }: LessonRetentionTableProps) {
  const [sortField, setSortField] = useState("retentionRate");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = (field: string) => {
    const newOrder = sortField === field && sortOrder === "desc" ? "asc" : "desc";
    setSortField(field);
    setSortOrder(newOrder);
    onSort?.(field, newOrder);
  };

  return (
    <div className="data-table-container">
      <div className="data-table-header">
        <h4 className="h6">Lesson Retention Summary</h4>
      </div>
      <div className="overflow-x-auto gradient-scrollbar">
        <table className="data-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort("lessonName")}>
                Lesson {sortField === "lessonName" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th className="sortable" onClick={() => handleSort("retentionRate")}>
                Retention Rate {sortField === "retentionRate" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Cards</th>
              <th className="sortable" onClick={() => handleSort("averageDifficulty")}>
                Difficulty {sortField === "averageDifficulty" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th className="sortable" onClick={() => handleSort("completionRate")}>
                Completion {sortField === "completionRate" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Last Review</th>
            </tr>
          </thead>
          <tbody>
            {lessons.map((lesson) => (
              <tr key={lesson.lessonId}>
                <td>
                  <div className="data-table-cell-name">{lesson.lessonName}</div>
                </td>
                <td>
                  <div className="flex items-center">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full mr-2 max-w-[100px]">
                      <div
                        className={`h-2 rounded-full ${
                          lesson.retentionRate >= 80
                            ? "bg-success-500"
                            : lesson.retentionRate >= 60
                              ? "bg-warning-500"
                              : "bg-error-500"
                        }`}
                        style={{ width: `${lesson.retentionRate}%` }}
                      />
                    </div>
                    <span className="caption">{lesson.retentionRate.toFixed(0)}%</span>
                  </div>
                </td>
                <td>
                  <span className="data-table-cell-caption">{lesson.totalCards}</span>
                </td>
                <td>
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      lesson.averageDifficulty >= 7
                        ? "badge-danger"
                        : lesson.averageDifficulty >= 4
                          ? "badge-accent"
                          : "badge-primary"
                    }`}
                  >
                    {lesson.averageDifficulty.toFixed(1)}
                  </span>
                </td>
                <td>
                  <div className="flex items-center">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full mr-2 max-w-[100px]">
                      <div
                        className="h-2 bg-primary-500 rounded-full"
                        style={{ width: `${lesson.completionRate}%` }}
                      />
                    </div>
                    <span className="caption">{lesson.completionRate.toFixed(0)}%</span>
                  </div>
                </td>
                <td>
                  <span className="data-table-cell-caption">
                    {lesson.lastReviewDate
                      ? new Date(lesson.lastReviewDate).toLocaleDateString()
                      : "—"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
